import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ClassRosterPane from '../components/ClassRosterPane';
import FilterButton from '../components/FilterButton';
import ManabaRosterImport from '../components/ManabaRosterImport';
import { usePageFileDrop, PageDropOverlay } from '../components/PageFileDrop';
import RosterImports from './RosterImports';
import StudentStatusBadges, { STATUS } from '../components/StudentStatusBadges';
import StudentCustomList, { ViewSwitch, useStudentListSettings } from '../components/StudentCustomList';
import DeviceCheckPanel from '../components/DeviceCheckPanel';

export default function StudentManagement({ user }) {
  const [students, setStudents]   = useState([]);
  const [classes, setClasses]     = useState([]);
  // 授業の選択は URL（?classId=）にも持つ【2026-09-17】
  //   授業ページの「受講者名簿」をここに統合したので、外からこの形でリンクされる。
  const [searchParams, setSearchParams] = useSearchParams();
  const urlClassId = searchParams.get('classId') || '';
  const [classFilter, setFilter]  = useState(urlClassId);
  // 授業を選んだときの名簿（出欠マトリクス）
  const [roster, setRoster]       = useState(null);
  const [msg, setMsg]             = useState('');
  // 全学生タブの状態で絞る【2026-09-17】'' | 'notOnRoster' | 'noAccount'
  const [statusFilter, setStatusFilter] = useState('');
  // 表示の形（標準 / カスタム）と並び。人ごとにサーバーへ保存する【2026-09-17】
  const [listSettings, updateListSettings, listStored] = useStudentListSettings();
  const listView = listSettings.view;
  const setListView = (v) => updateListSettings({ view: v });
  const listSort = listSettings.sort;
  const [listQuery, setListQuery] = useState('');
  // 年度【2026-09-17】画面上部で常に切り替える。
  //   '' は「まだ既定値（今の年度）を取りに行っている途中」。取れるまで一覧は読まない。
  //   'all' = すべての年度 / 'hold' = 年度保留中の授業 / '2026' など
  const [yearSel, setYearSel]     = useState('');
  const [yearOptions, setYearOptions] = useState([]);
  const [heldCount, setHeldCount] = useState(0);
  const [threshold, setThreshold] = useState(70);
  // 【2026-09-04】実施回数が少ないうちの出席率は意味を持たないので、
  //   出席率での判定は「実施 minSessions 回以上」に限る。
  //   代わりに累計欠席・連続欠席でも拾う（学期はじめから機能する）。
  const [minSessions, setMinSessions]   = useState(4);
  const [absentCount, setAbsentCount]   = useState(3);
  const [consecutive, setConsecutive]   = useState(2);
  // 名簿にはいるがアプリ未登録の学生も要注意に出すか【2026-09-07】
  const [includeUnreg, setIncludeUnreg] = useState(true);
  const [tab, setTab]             = useState('all'); // all | low | anomalies | merge | outside | import
  // 名簿に載っていないのに出席している学生【2026-09-07】
  const [outside, setOutside]     = useState(null);
  const [low, setLow]             = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  // 端末の確認を開いている学生 { id, label }【2026-10-01】
  const [deviceFor, setDeviceFor] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState('');
  // 氏名の手動修正【2026-09-04】
  const [editing, setEditing]     = useState(null); // { id, student_id, name }
  const [editName, setEditName]   = useState('');
  const [saving, setSaving]       = useState(false);
  // 仮アカウント統合【2026-09-04】教務・管理者のみ
  const canMerge = !!user && (user.role === 'academic_staff' || user.role === 'developer');
  const [mergeItems, setMergeItems] = useState([]);
  const [mergeMsg, setMergeMsg]     = useState('');
  const [mergeBusy, setMergeBusy]   = useState(false);
  const [residual, setResidual]     = useState(null); // { placeholderId, targetId, rows }

  const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
  const authJson = () => ({ ...auth(), 'Content-Type': 'application/json' });
  const token = () => localStorage.getItem('token');
  const isStaff = !!user && (user.role === 'academic_staff' || user.role === 'developer');
  // すべての授業のときの名簿取り込み（開閉）【2026-09-17】
  const [importOpen, setImportOpen] = useState(false);
  // 画面のどこに名簿ファイルを落としても取り込めるようにする【2026-09-17】
  //   授業を選んでいればその授業の名簿へ、選んでいなければ「すべての授業」の取り込みへ渡す。
  //   仮アカウント統合タブは取り込みと関係ないので受けない。
  const [incomingFile, setIncomingFile] = useState(null);
  const pageDragging = usePageFileDrop((files) => {
    const f = files[0];
    if (!/\.(csv|tsv|xls|xlsx|xlsm|ods)$/i.test(f.name)) {
      setErr(f.name + '： 名簿は Excel（.xls / .xlsx）・.ods・CSV のどれかを落としてください');
      return;
    }
    setErr('');
    setMsg(files.length > 1 ? `ファイルが${files.length}個あったので、最初の「${f.name}」だけを読みます` : '');
    if (tab !== 'all' && tab !== 'import') setTab('all');
    if (!classFilter && tab !== 'import') setImportOpen(true);
    setIncomingFile(f);
  }, { enabled: tab !== 'merge' });
  const incomingProps = { incomingFile, onIncomingTaken: () => setIncomingFile(null) };

  const afterImport = () => {
    loadClasses();
    if (classFilter) loadRoster(true); else if (tab === 'all') loadAll();
  };

  // 年度の選択肢と既定値（4月始まりの今の年度）
  useEffect(() => {
    fetch('/api/terms', { headers: auth() })
      .then(r => r.json())
      .then(d => {
        if (!d.success) { setYearSel('all'); return; }
        setYearOptions((d.yearOptions || []).slice().sort((a, b) => b - a));
        const fallback = String(d.defaults?.academicYear ?? 'all');
        if (!urlClassId) { setYearSel(fallback); return; }
        // リンクで授業が指定されたら、その授業の年度を開く
        fetch('/api/schedule/classes/overview?academicYear=all&includeArchived=1', { headers: auth() })
          .then(r => r.json())
          .then(o => {
            let c = (o.classes || []).find(x => String(x.id) === String(urlClassId));
            if (!c) { setYearSel(fallback); return; }
            // ほかの授業にまとめられた授業なら、まとめ先を開く【2026-09-17】
            if (c.linked_to_class_id) {
              const p = (o.classes || []).find(x => String(x.id) === String(c.linked_to_class_id));
              if (p) { c = p; setFilter(String(p.id)); }
            }
            setYearSel(c.year_on_hold ? 'hold' : (c.academic_year ? String(c.academic_year) : 'all'));
          })
          .catch(() => setYearSel(fallback));
      })
      .catch(() => setYearSel('all'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ブラウザの戻る／進む・外からのリンクで URL の授業が変わったら追従する
  useEffect(() => { setFilter(urlClassId); }, [urlClassId]);

  // 選んだ授業を URL に残す（戻る・リロード・共有で同じ授業が開く）
  useEffect(() => {
    if (String(classFilter || '') === String(urlClassId || '')) return;
    const next = new URLSearchParams(searchParams);
    if (classFilter) next.set('classId', String(classFilter)); else next.delete('classId');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classFilter]);

  // その年度の授業（1授業ずつ切り替えるための一覧）
  //   /classes は曜日時限の数だけ行が増えるので、一覧専用の overview を使う。
  //   過去の年度を見るときは畳んだ授業も要るので含める。
  const loadClasses = () => {
    if (!yearSel) return;
    // まとめた子の授業は選択肢に出さない（まとめ先の名簿に入っている）
    const qs = new URLSearchParams({ academicYear: yearSel, includeArchived: '1', excludeLinked: '1' });
    fetch('/api/schedule/classes/overview?' + qs, { headers: auth() })
      .then(r => r.json())
      .then(d => {
        const list = d.classes || d.data || [];
        setClasses(list);
        setHeldCount(d.heldCount || 0);
        // 年度を変えたら、その年度に無い授業の選択は外す
        setFilter(prev => (prev && !list.some(c => String(c.id) === String(prev)) ? '' : prev));
      })
      .catch(() => setClasses([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadClasses(); }, [yearSel]);

  // 授業を1つ選んだときの名簿（旧・授業ページの受講者名簿）【2026-09-17】
  //   quiet=true は追加・取消のあとの再読み込み（画面を「読み込み中」にしない）
  const loadRoster = async (quiet = false) => {
    if (!classFilter) { setRoster(null); return; }
    if (!quiet) setLoading(true);
    setErr('');
    try {
      const res = await fetch('/api/schedule/classes/' + classFilter + '/attendance-matrix', { headers: auth() });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '名簿の取得に失敗しました');
      if (d.class && d.class.linked_to_class_id) {
        // まとめられた授業を開こうとしたら、まとめ先へ切り替える
        setMsg(`「${d.class.name}」は「${d.class.linked_to_name || 'ほかの授業'}」にまとめられているので、まとめ先の名簿を開きました`);
        setFilter(String(d.class.linked_to_class_id));
        return;
      }
      setRoster(d);
    } catch (e) { setErr(e.message); setRoster(null); }
    finally     { if (!quiet) setLoading(false); }
  };

  // 一覧APIに付ける年度の指定
  const withYear = (params) => {
    if (yearSel) params.append('academicYear', yearSel);
    return params;
  };

  const loadAll = async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      params.append('limit', '500');
      if (classFilter) params.append('classId', classFilter);
      withYear(params);
      const res = await fetch('/api/students?' + params, { headers: auth() });
      const d   = await res.json();
      setStudents(d.students || d.data || []);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };

  const loadLow = async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      params.append('threshold', threshold);
      params.append('minSessions', minSessions);
      params.append('absentCount', absentCount);
      params.append('consecutive', consecutive);
      if (!includeUnreg) params.append('includeUnregistered', '0');
      if (classFilter) params.append('classId', classFilter);
      withYear(params);
      const res = await fetch('/api/students/low-attendance?' + params, { headers: auth() });
      const d   = await res.json();
      setLow(d.students || d.data || []);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };

  const loadAnomalies = async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (classFilter) params.append('class_id', classFilter);
      withYear(params);
      const res = await fetch('/api/students/device-anomalies?' + params, { headers: auth() });
      const d   = await res.json();
      setAnomalies(d.anomalies || d.data || []);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };

  // ── 仮アカウント統合【2026-09-04】────────────────────────────
  // manaba名簿で作った仮アカウントと、本人がサインアップした実アカウントが
  // 自動照合で繋がらなかったケースを、人が判断して繋ぐ。
  // 名簿外の出席（授業をまたいでまとめて）【2026-09-07】
  const loadOutside = async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (classFilter) params.append('classId', classFilter);
      withYear(params);
      const res = await fetch('/api/students/outside-roster?' + params, { headers: auth() });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '取得に失敗しました');
      setOutside(d);
    } catch (e) { setErr(e.message); setOutside(null); }
    finally     { setLoading(false); }
  };

  const loadMerge = async () => {
    setLoading(true); setErr(''); setMergeMsg(''); setResidual(null);
    try {
      const res = await fetch('/api/students/merge-candidates', { headers: auth() });
      const d   = await res.json();
      if (!d.success) { setErr(d.message || '統合候補の取得に失敗しました'); return; }
      setMergeItems(d.items || []);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };

  // 照合用学籍番号が完全一致する分をまとめて統合する。
  // 名簿インポート直後にも同じ処理が自動で走るので、これは取りこぼしの繋ぎ直し用。
  const doAutoMerge = async () => {
    const ok = window.confirm(
      '照合用学籍番号が完全に一致する仮アカウントを、まとめて本人のアカウントに統合します。\n' +
      '一致しているものだけが対象です。よろしいですか？'
    );
    if (!ok) return;
    setMergeBusy(true); setErr(''); setMergeMsg('');
    try {
      const res = await fetch('/api/students/merge/auto', { method: 'POST', headers: auth() });
      const d   = await res.json();
      if (!d.success) { setErr(d.message || '一括統合に失敗しました'); return; }
      setMergeMsg(
        `${d.mergedCount}件を統合しました` +
        (d.ambiguousCount > 0 ? `（同じ学籍番号の本アカウントが複数あるため ${d.ambiguousCount}件は保留）` : '')
      );
      loadMerge();
    } catch (e) { setErr(e.message); }
    finally     { setMergeBusy(false); }
  };

  const doMerge = async (placeholderId, targetId, confirmResidual = false) => {
    setMergeBusy(true); setErr(''); setMergeMsg('');
    try {
      const res = await fetch('/api/students/merge', {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ placeholderId, targetId, confirmResidual })
      });
      const d = await res.json();

      if (!d.success && d.code === 'RESIDUAL_REFERENCES') {
        // 付け替え対象外のデータが残っている。内訳を見せてから再確認する。
        setResidual({ placeholderId, targetId, rows: d.residual || [] });
        return;
      }
      if (!d.success) { setErr(d.message || '統合に失敗しました'); return; }

      const r = d.result;
      setMergeMsg(
        `統合しました: ${r.placeholder.name}(${r.placeholder.studentId}) → ${r.target.name}(${r.target.studentId})　` +
        `履修 ${r.enrollmentsMoved}件 / 出席 ${r.attendanceMoved}件 を移しました` +
        (r.matchIdMoved ? ' / 照合用学籍番号も引き継ぎ' : '') +
        (r.nameMoved ? ' / 表示名も名簿の値に更新' : r.rosterNameMoved ? ' / 名簿の氏名も引き継ぎ' : '')
      );
      setResidual(null);
      loadMerge();
    } catch (e) { setErr(e.message); }
    finally     { setMergeBusy(false); }
  };

  useEffect(() => {
    if (!yearSel) return;   // 既定の年度が決まるまで待つ（二重読み込みを避ける）
    setMsg('');
    if (tab === 'all')        { if (classFilter) loadRoster(); else loadAll(); }
    if (tab === 'low')        loadLow();
    if (tab === 'anomalies')  loadAnomalies();
    if (tab === 'merge')      loadMerge();
    if (tab === 'outside')    loadOutside();
    // eslint-disable-next-line
  }, [tab, classFilter, yearSel, threshold, minSessions, absentCount, consecutive, includeUnreg]);

  // ── 氏名の手動修正 ──────────────────────────────────────────
  // 学籍番号は正しいのに氏名が意味をなさない学生を、教員・教務が直すための口。
  // 保存すると name_source が 'manual' になり、以降の名簿インポートで
  // 上書きされなくなる。変更履歴は「変更履歴」画面（/users-audit）に残る。
  const startEdit = (s) => { setEditing(s); setEditName(s.name || ''); setErr(''); };
  const cancelEdit = () => { setEditing(null); setEditName(''); };

  const saveName = async () => {
    if (!editing) return;
    const trimmed = editName.trim();
    if (!trimmed) { setErr('氏名を空にすることはできません'); return; }
    setSaving(true);
    setErr('');
    try {
      const res = await fetch(`/api/students/${editing.id}/name`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed })
      });
      const data = await res.json();
      if (!data.success) { setErr(data.message || '氏名の更新に失敗しました'); return; }
      setStudents(prev => prev.map(x =>
        x.id === editing.id ? { ...x, name: data.student.name, name_source: 'manual' } : x
      ));
      cancelEdit();
    } catch (e) {
      setErr('通信エラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  // 絞り込みの既定値【2026-09-11】
  // 「既定と違うものだけ」をタグに出すので、ここが基準になる
  //   年度・授業は画面上部で常に見えているので、ここ（漏斗）には入れない【2026-09-17】
  const FILTER_DEFAULTS = { threshold: 70, minSessions: 4,
                            absentCount: 3, consecutive: 2, includeUnreg: true };
  const resetFilters = () => {
    setThreshold(FILTER_DEFAULTS.threshold);
    setMinSessions(FILTER_DEFAULTS.minSessions);
    setAbsentCount(FILTER_DEFAULTS.absentCount);
    setConsecutive(FILTER_DEFAULTS.consecutive);
    setIncludeUnreg(FILTER_DEFAULTS.includeUnreg);
  };

  const filterChips = [];

  // 全学生タブの状態別の件数と絞り込み【2026-09-17】
  const counts = {
    notOnRoster: students.filter(s => s.is_outside_roster).length,
    noAccount:   students.filter(s => s.is_placeholder).length,
  };
  const shownStudents = statusFilter === 'notOnRoster' ? students.filter(s => s.is_outside_roster)
                      : statusFilter === 'noAccount'   ? students.filter(s => s.is_placeholder)
                      : students;
  // 検索（氏名・名簿の氏名・学籍番号）
  const lq = listQuery.trim().toLowerCase();
  const listed = !lq ? shownStudents : shownStudents.filter(s =>
    [s.name, s.roster_name, s.student_id, s.report_student_id, s.self_reported_name]
      .some(v => String(v || '').toLowerCase().includes(lq)));
  const LIST_SORTERS = {
    student_id: (a, b) => String(a.report_student_id || a.student_id || '').localeCompare(String(b.report_student_id || b.student_id || ''), 'ja', { numeric: true }),
    name:       (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ja'),
    rate:       (a, b) => (a.average_attendance_rate == null ? 999 : Number(a.average_attendance_rate))
                        - (b.average_attendance_rate == null ? 999 : Number(b.average_attendance_rate)),
    absent:     (a, b) => Number(a.absent_sessions || 0) - Number(b.absent_sessions || 0),
    streak:     (a, b) => Number(a.max_consecutive_absences || 0) - Number(b.max_consecutive_absences || 0),
  };
  const sorter = LIST_SORTERS[listSort.key] || LIST_SORTERS.student_id;
  const customItems = [...listed]
    .sort((a, b) => sorter(a, b) * listSort.dir || LIST_SORTERS.student_id(a, b))
    .map(s => ({
      key: s.id || s.student_id,
      raw: s,
      studentId: s.report_student_id || s.student_id,
      name: s.name,
      rate: s.average_attendance_rate != null ? Number(s.average_attendance_rate) : null,
      placeholder: s.is_placeholder,
      outside: s.is_outside_roster,
      // カスタム表示で ON/OFF できる項目の値
      v: {
        attended: s.total_attendance, held: s.held_sessions, absent: s.absent_sessions, late: s.late_sessions,
        streak: s.max_consecutive_absences, last: s.last_attended || '',
        since: s.created_at ? String(s.created_at).slice(0, 10) : '',
        rosterName: s.roster_name,
      },
    }));
  const renderNameEdit = (it) => (editing && editing.id === it.raw.id ? (
    <span style={{ display: 'inline-flex', gap: '0.3rem', alignItems: 'center' }}>
      <input value={editName} autoFocus aria-label="氏名"
             onChange={e => setEditName(e.target.value)}
             onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') cancelEdit(); }}
             style={{ padding: '0.1rem 0.3rem', width: 130 }} />
      <button onClick={saveName} disabled={saving}>{saving ? '保存中' : '保存'}</button>
      <button onClick={cancelEdit} disabled={saving}>取消</button>
    </span>
  ) : (
    <button onClick={() => startEdit(it.raw)} style={{ fontSize: '0.76rem', padding: '0 0.45rem' }}>修正</button>
  ));

  // ── 年度・授業の切り替え【2026-09-17】────────────────────────
  const DAY = ['日', '月', '火', '水', '木', '金', '土'];
  const classLabel = (c) => {
    const when = (c.schedules || []).map(x => DAY[x.day_of_week] + String(x.start_time || '').slice(0, 5)).join('・');
    const term = yearSel === 'all' || yearSel === 'hold'
      ? `${c.academic_year ? c.academic_year + '年度' : ''}${c.term_name || ''}`
      : (c.term_name || '');
    return c.name + (term || when ? `（${[term, when].filter(Boolean).join(' ')}）` : '')
         + (c.archived ? ' ［畳んだ授業］' : '');
  };
  const classIdx = classes.findIndex(c => String(c.id) === String(classFilter));
  const selectedClass = classIdx >= 0 ? classes[classIdx] : null;
  // 前後の授業へ。「すべて」の前後は先頭・末尾
  const stepClass = (d) => {
    if (classes.length === 0) return;
    let next;
    if (classIdx < 0) next = d > 0 ? 0 : classes.length - 1;
    else next = classIdx + d;
    if (next < 0 || next >= classes.length) { setFilter(''); return; }
    setFilter(String(classes[next].id));
  };
  const yearText = yearSel === 'all' ? 'すべての年度'
                 : yearSel === 'hold' ? '年度保留中の授業'
                 : yearSel ? yearSel + '年度' : '';
  if (tab === 'low') {
    if (Number(threshold)   !== FILTER_DEFAULTS.threshold)
      filterChips.push({ label: '出席率 ' + threshold + '% 未満',
                         onClear: () => setThreshold(FILTER_DEFAULTS.threshold) });
    if (Number(minSessions) !== FILTER_DEFAULTS.minSessions)
      filterChips.push({ label: '最低実施 ' + minSessions + '回',
                         onClear: () => setMinSessions(FILTER_DEFAULTS.minSessions) });
    if (Number(absentCount) !== FILTER_DEFAULTS.absentCount)
      filterChips.push({ label: '累計欠席 ' + absentCount + '回以上',
                         onClear: () => setAbsentCount(FILTER_DEFAULTS.absentCount) });
    if (Number(consecutive) !== FILTER_DEFAULTS.consecutive)
      filterChips.push({ label: '連続欠席 ' + consecutive + '回以上',
                         onClear: () => setConsecutive(FILTER_DEFAULTS.consecutive) });
    if (includeUnreg !== FILTER_DEFAULTS.includeUnreg)
      filterChips.push({ label: STATUS.noAccount.label + 'の学生を除く',
                         onClear: () => setIncludeUnreg(FILTER_DEFAULTS.includeUnreg) });
  }

  return (
    <div className="page-container page-wide">
      <PageDropOverlay
        show={pageDragging}
        title="ファイルをドロップして名簿を読み込む"
        note={classFilter && selectedClass
          ? `「${selectedClass.name}」の名簿の取り込みに渡します（中身を確認してから保存します）`
          : 'manaba の履修者名簿として読み込みます（中身を確認してから保存します）'}
      />
      <div className="page-header">
        <h1>学生管理</h1>
        <p>履修学生の出席状況を確認・管理します。</p>
      </div>

      {/* 年度と授業【2026-09-17】
          学生を「どの年度の、どの授業の学生か」で分けて見る。
          年度は絞り込みの漏斗に畳まず、常に見えるところに置く（今どの年度を見ているかが一番大事）。 */}
      <div className="card" style={{ padding: '0.7rem 0.9rem', marginBottom: '0.8rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>年度</label>
          <select value={yearSel} onChange={e => setYearSel(e.target.value)}
                  style={{ padding: '0.35rem', fontWeight: 700 }}>
            {!yearSel && <option value="">（読み込み中）</option>}
            {yearOptions.map(y => <option key={y} value={String(y)}>{y}年度</option>)}
            {yearSel && !['all', 'hold'].includes(yearSel) && !yearOptions.map(String).includes(yearSel) && (
              <option value={yearSel}>{yearSel}年度</option>
            )}
            <option value="all">すべての年度</option>
            <option value="hold">年度保留中{heldCount ? `（${heldCount}）` : ''}</option>
          </select>

          <label style={{ fontSize: '0.85rem', fontWeight: 700, marginLeft: '0.6rem' }}>授業</label>
          <button className="btn-secondary" style={{ padding: '0.2rem 0.55rem' }}
                  onClick={() => stepClass(-1)} disabled={classes.length === 0} title="前の授業">◀</button>
          <select value={classFilter} onChange={e => setFilter(e.target.value)}
                  style={{ padding: '0.35rem', minWidth: 240, maxWidth: '100%' }}>
            <option value="">— {yearText}のすべての授業（{classes.length}） —</option>
            {classes.map(c => <option key={c.id} value={c.id}>{classLabel(c)}</option>)}
          </select>
          <button className="btn-secondary" style={{ padding: '0.2rem 0.55rem' }}
                  onClick={() => stepClass(1)} disabled={classes.length === 0} title="次の授業">▶</button>
        </div>

        {selectedClass && (
          <div style={{ fontSize: '0.82rem', color: '#555', marginTop: '0.45rem',
                        display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>
              {selectedClass.year_on_hold
                ? <strong style={{ color: '#b45309' }}>年度保留中</strong>
                : <>{selectedClass.academic_year ? selectedClass.academic_year + '年度' : '年度未設定'}{selectedClass.term_name || ''}</>}
              {selectedClass.teacher_name && <> ・ {selectedClass.teacher_name}</>}
            </span>
            <span>履修 <strong>{selectedClass.enrolled_count}</strong>人
              {selectedClass.placeholder_count > 0 && (
                <span style={{ color: STATUS.noAccount.color }} title={STATUS.noAccount.title}>
                  （うち{STATUS.noAccount.label} {selectedClass.placeholder_count}）
                </span>
              )}
            </span>
            <span>実施 <strong>{selectedClass.held_sessions}</strong>回</span>
            <Link to={'/classes/' + selectedClass.id}>授業のページを開く</Link>
          </div>
        )}
        {yearSel === 'hold' && (
          <div style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.45rem' }}>
            年度が怪しいので保留にした授業です。正しい年度は「名簿の取り込み」タブの取り込み履歴から決められます。
          </div>
        )}
        {yearSel && yearSel !== 'hold' && heldCount > 0 && (
          <div style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.45rem' }}>
            年度保留中の授業が {heldCount}件 あります（この一覧には含まれません）。
          </div>
        )}
      </div>

      {/* タブ */}
      <div className="tab-bar">
        <button className={'tab-btn' + (tab==='all'?' active':'')}        onClick={()=>setTab('all')}>
          {classFilter ? '受講者名簿' : '全学生'}
        </button>
        <button className={'tab-btn' + (tab==='low'?' active':'')}        onClick={()=>setTab('low')}>低出席率</button>
        <button className={'tab-btn' + (tab==='anomalies'?' active':'')} onClick={()=>setTab('anomalies')}>デバイス共有疑い</button>
        <button className={'tab-btn' + (tab==='outside'?' active':'')}   onClick={()=>setTab('outside')}
                title="名簿に載っていないのに出席している学生を、授業をまたいでまとめて出します">
          名簿未登録の出席
        </button>
        <button className={'tab-btn' + (tab==='import'?' active':'')}    onClick={()=>setTab('import')}>名簿の取り込み</button>
        {canMerge && (
          <button className={'tab-btn' + (tab==='merge'?' active':'')} onClick={()=>setTab('merge')}>仮アカウント統合</button>
        )}
      </div>

      {/* 絞り込み（取り込みタブでは使わない）【2026-09-11 漏斗ボタンに畳んだ】
          入力欄を常に出しているとカード1枚ぶん場所を取るので、漏斗に畳む。
          ただし畳んだことで「絞り込まれているのに気づかない」のが一番まずいので、
          効いている条件は閉じていてもタグで出す。 */}
      {tab !== 'import' && (
        <div style={{ margin: '0 0 1rem' }}>
          <FilterButton chips={filterChips} onReset={resetFilters}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {tab === 'low' ? (
                <>
                  <div>
                    <label style={{display:'block',fontSize:'0.85rem',marginBottom:'0.25rem'}}>出席率閾値 (%)</label>
                    <input type="number" value={threshold} onChange={e=>setThreshold(e.target.value)} style={{width:100}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:'0.85rem',marginBottom:'0.25rem'}}
                           title="これ未満しか実施していない授業は、出席率では判定しません">
                      出席率で見る最低実施回数
                    </label>
                    <input type="number" min="1" value={minSessions} onChange={e=>setMinSessions(e.target.value)} style={{width:100}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:'0.85rem',marginBottom:'0.25rem'}}>累計欠席（回以上）</label>
                    <input type="number" min="1" value={absentCount} onChange={e=>setAbsentCount(e.target.value)} style={{width:100}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:'0.85rem',marginBottom:'0.25rem'}}>連続欠席（回以上）</label>
                    <input type="number" min="1" value={consecutive} onChange={e=>setConsecutive(e.target.value)} style={{width:100}}/>
                  </div>
                  <label style={{display:'flex',gap:'0.35rem',alignItems:'center',fontSize:'0.85rem'}}
                         title={STATUS.noAccount.title}>
                    <input type="checkbox" checked={includeUnreg}
                           onChange={e=>setIncludeUnreg(e.target.checked)} />
                    {STATUS.noAccount.label}の学生も含める
                  </label>
                </>
              ) : (
                <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                  出席率まわりの条件は「低出席率」タブで使います。
                  年度と授業は画面上部で切り替えます。
                </div>
              )}
            </div>
          </FilterButton>
        </div>
      )}

      {err && <div className="card" style={{borderLeft:'4px solid #ef4444'}}><p style={{color:'#c62828'}}>{err}</p></div>}
      {msg && <div className="card" style={{borderLeft:'4px solid #22c55e'}}><p style={{color:'#166534'}}>{msg}</p></div>}

      {/* 授業を選んでいるときは、その授業の受講者名簿【2026-09-17 授業ページから統合】
          追加・取り消し・manaba 名簿の取り込み・氏名の修正・出欠表まで、ここで完結する */}
      {!loading && tab === 'all' && classFilter && roster && (
        <ClassRosterPane
          key={classFilter}
          classId={classFilter}
          auth={authJson}
          token={token}
          reload={() => { loadRoster(true); loadClasses(); }}
          setErr={setErr}
          setMsg={setMsg}
          sessions={roster.sessions || []}
          students={roster.students || []}
          held={roster.heldSessions || 0}
          classes={classes}
          cls={roster.class}
          isStaff={isStaff}
          {...incomingProps}
        />
      )}

      {loading && <div className="card"><p>読み込み中...</p></div>}

      {/* 名簿の取り込み（コース名ごとに授業へ振り分ける）【2026-09-07】 */}
      {tab === 'import' && (
        <>
          <div className="card">
            <ManabaRosterImport
              classes={classes}
              token={token}
              setErr={setErr}
              setMsg={setMsg}
              onImported={afterImport}
              isStaff={isStaff}
              defaultYear={yearSel}
              {...incomingProps}
            />
          </div>
          {/* 取り込んだファイルごとに、あとから年度を直す・取り消す【2026-09-17】 */}
          <div className="card">
            <RosterImports embedded />
          </div>
        </>
      )}

      {/* 名簿外の出席【2026-09-07】
          名簿を取り込んでいない授業・取り込み漏れを、授業をまたいで拾う。 */}
      {!loading && tab === 'outside' && (
        <div className="card">
          <div className="card-header">
            <h2>名簿未登録の出席{outside ? '（' + outside.total + '件 / ' + outside.classCount + '授業）' : ''}</h2>
          </div>
          <p style={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.8, marginTop: 0 }}>
            履修登録が無いのに打刻がある学生です。名簿をまだ取り込んでいない授業、
            取り込み漏れ、別の授業での打刻などが原因です。
            名簿を取り込めば履修者に入り、ここから消えます。
          </p>

          {!outside || outside.classes.length === 0 ? (
            <p style={{ color: '#888' }}>該当なし。すべての出席が名簿に載っています。</p>
          ) : outside.classes.map(c => (
            <div key={c.classId} style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                            flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                <strong>{c.className}</strong>
                {c.rosterMissing ? (
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#92400e',
                                 background: '#fef3c7', padding: '0.1rem 0.4rem', borderRadius: 4 }}>
                    名簿が未取り込み
                  </span>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: '#888' }}>
                    履修者 {c.rosterSize}人
                  </span>
                )}
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  名簿未登録 {c.students.length}人
                </span>
              </div>
              <table className="data-table">
                <thead>
                  <tr><th>学籍番号</th><th>氏名</th><th>出席回数</th><th>初回</th><th>最終</th></tr>
                </thead>
                <tbody>
                  {c.students.map(s => (
                    <tr key={s.id}>
                      <td>{s.student_id}</td>
                      <td>
                        {s.name}
                        <StudentStatusBadges placeholder={s.is_placeholder} />
                      </td>
                      <td>{s.times}</td>
                      <td>{String(s.first_date || '').slice(0, 10)}</td>
                      <td>{String(s.last_date || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {outside && outside.rosterMissingCount > 0 && (
            <p style={{ fontSize: '0.85rem', marginTop: '1rem' }}>
              名簿がまるごと未取り込みの授業が {outside.rosterMissingCount} つあります。
              <button className="btn-secondary" style={{ marginLeft: '0.5rem' }}
                      onClick={() => setTab('import')}>
                名簿を取り込む
              </button>
            </p>
          )}
        </div>
      )}

      {/* 全学生 */}
      {/* すべての授業のときも manaba の名簿を取り込める【2026-09-17】
          読み込み中の表示で消えないよう、loading の外に置く（入力途中の状態を失わない） */}
      {tab === 'all' && !classFilter && (
        <div className="card" style={{ padding: importOpen ? undefined : '0.6rem 0.9rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button type="button" className={importOpen ? 'btn-secondary' : 'btn-primary'}
                    onClick={() => setImportOpen(o => !o)}>
              {importOpen ? '▲ 取り込みを閉じる' : '＋ manabaの名簿を取り込む'}
            </button>
            {!importOpen && (
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                複数コースが入ったファイルでも、コースごとに行き先の授業を選んで取り込めます
              </span>
            )}
          </div>
          {importOpen && (
            <div style={{ marginTop: '0.7rem' }}>
              <ManabaRosterImport
                classes={classes}
                token={token}
                setErr={setErr}
                setMsg={setMsg}
                onImported={afterImport}
                isStaff={isStaff}
                defaultYear={yearSel}
                {...incomingProps}
              />
            </div>
          )}
        </div>
      )}

      {!loading && tab === 'all' && !classFilter && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>
              {selectedClass ? `「${selectedClass.name}」の学生` : `${yearText}の学生`} ({students.length})
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
              <input value={listQuery} onChange={e => setListQuery(e.target.value)}
                     placeholder="氏名・学籍番号で検索" aria-label="学生を検索"
                     style={{ padding: '0.3rem 0.5rem', minWidth: 170 }} />
              <ViewSwitch value={listView} onChange={setListView} />
            </div>
          </div>
          {/* 状態で絞る【2026-09-17】
              「名簿未登録」と「アカウント未作成」は対処が違うので、別々に拾えるようにする */}
          <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', alignItems:'center', margin:'0 0 0.6rem' }}>
            {[
              { key: '',            label: 'すべて',                  n: students.length },
              { key: 'notOnRoster', label: STATUS.notOnRoster.label, n: counts.notOnRoster, color: STATUS.notOnRoster.color },
              { key: 'noAccount',   label: STATUS.noAccount.label,   n: counts.noAccount,   color: STATUS.noAccount.color },
            ].map(o => (
              <button key={o.key || 'all'} type="button"
                      onClick={() => setStatusFilter(o.key)}
                      title={o.key ? STATUS[o.key].title : undefined}
                      style={{
                        fontSize:'0.8rem', padding:'0.15rem 0.6rem', borderRadius:999, cursor:'pointer',
                        border:'1px solid ' + (o.color || '#9ca3af'),
                        background: statusFilter === o.key ? (o.color || '#374151') : '#fff',
                        color: statusFilter === o.key ? '#fff' : (o.color || '#374151'),
                      }}>
                {o.label} {o.n}
              </button>
            ))}
          </div>
          <p style={{ fontSize:'0.78rem', color:'#888', margin:'0 0 0.6rem', lineHeight:1.7 }}>
            <strong style={{color:STATUS.notOnRoster.color}}>{STATUS.notOnRoster.label}</strong>：打刻はあるが履修者名簿に載っていない（名簿の取り込み・履修者追加で解消）
            <br/>
            <strong style={{color:STATUS.noAccount.color}}>{STATUS.noAccount.label}</strong>：名簿には載っているが本人がまだアプリに登録していない（本人の登録で解消）
          </p>
          {listed.length === 0 ? <p style={{color:'#888'}}>{students.length === 0 ? '学生がいません' : '該当する学生はいません'}</p>
          : listView === 'custom' ? (
            <StudentCustomList
              items={customItems}
              settings={listSettings}
              update={updateListSettings}
              stored={listStored}
              sortKeys={['student_id', 'name', 'rate', 'absent', 'streak']}
              renderEdit={renderNameEdit}
              sinceLabel="登録日"
            />
          ) : (
            <>
            {deviceFor && tab === 'all' && (
              <DeviceCheckPanel userId={deviceFor.id} label={deviceFor.label} onClose={() => setDeviceFor(null)} />
            )}
            <table className="data-table">
              <thead><tr><th>学籍番号</th><th>氏名</th><th>メール</th><th>{selectedClass ? '履修' : '登録授業'}</th><th>出席率</th><th></th></tr></thead>
              <tbody>{listed.map(s => (
                <tr key={s.id || s.student_id}>
                  <td>
                    {s.report_student_id || s.student_id}
                    {s.report_student_id && s.report_student_id !== s.student_id && (
                      <div style={{fontSize:'0.75rem',color:'#999'}}>ログインID: {s.student_id}</div>
                    )}
                  </td>
                  <td>
                    {editing && editing.id === s.id ? (
                      <div style={{display:'flex',gap:'0.4rem',alignItems:'center'}}>
                        <input
                          value={editName}
                          onChange={e=>setEditName(e.target.value)}
                          onKeyDown={e=>{ if(e.key==='Enter') saveName(); if(e.key==='Escape') cancelEdit(); }}
                          autoFocus
                          style={{padding:'0.25rem 0.4rem',minWidth:160}}
                        />
                        <button onClick={saveName} disabled={saving}>{saving ? '保存中' : '保存'}</button>
                        <button onClick={cancelEdit} disabled={saving}>取消</button>
                      </div>
                    ) : (
                      <>
                        <strong>{s.name}</strong>
                        <StudentStatusBadges placeholder={s.is_placeholder} outside={s.is_outside_roster} />
                        {/* 所属・年次（教務システムの名簿から）【2026-09-28】 */}
                        {(s.affiliation || s.grade_year) && (
                          <div style={{fontSize:'0.75rem',color:'#6b7280'}}>
                            {s.affiliation}{s.affiliation && s.grade_year ? ' ・ ' : ''}{s.grade_year ? s.grade_year + '年次' : ''}
                          </div>
                        )}
                        {s.name_source === 'manual' && (
                          <span title="手動で修正済み。名簿インポートでも上書きされません"
                                style={{marginLeft:'0.4rem',fontSize:'0.7rem',color:'#0a7',border:'1px solid #0a7',borderRadius:4,padding:'0 4px'}}>
                            修正済
                          </span>
                        )}
                        {s.roster_name && s.roster_name !== s.name && (
                          <div style={{fontSize:'0.75rem',color:'#c67'}}>名簿: {s.roster_name}</div>
                        )}
                        {s.self_reported_name && s.self_reported_name !== s.name && (
                          <div style={{fontSize:'0.75rem',color:'#999'}}>本人入力: {s.self_reported_name}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td style={{fontSize:'0.85rem',color:'#666'}}>{s.email || '-'}</td>
                  <td>
                    {selectedClass
                      ? (s.is_outside_roster ? <span style={{color:STATUS.notOnRoster.color}}>{STATUS.notOnRoster.label}</span> : '履修中')
                      : (s.enrolled_classes ?? '-')}
                  </td>
                  <td>
                    {s.average_attendance_rate != null
                      ? <strong>{s.average_attendance_rate}%</strong>
                      : <span title="まだ実施した授業がないため、出席率は出せません" style={{color:'#999'}}>-</span>}
                    {s.held_sessions != null && (
                      <div style={{fontSize:'0.72rem',color:'#999'}}>実施 {s.held_sessions}回</div>
                    )}
                  </td>
                  <td>
                    {(!editing || editing.id !== s.id) && (
                      <button onClick={()=>startEdit(s)} title="氏名を修正">修正</button>
                    )}
                    <button onClick={()=>setDeviceFor({ id: s.id, label: `${s.name}（${s.student_id}）` })}
                            title="端末の確認・要注意にする" style={{marginLeft:4}}>端末</button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
            </>
          )}
        </div>
      )}

      {/* 仮アカウント統合【2026-09-04】 */}
      {!loading && tab === 'merge' && (
        <div className="card">
          <div className="card-header"><h2>仮アカウント統合 ({mergeItems.length})</h2></div>

          <p style={{fontSize:'0.9rem',color:'#555',lineHeight:1.7}}>
            manabaの名簿インポートで作られた「ログイン不可の仮アカウント」の一覧です。
            本人がサインアップすると学籍番号で自動的に引き継がれますが、名簿と違う番号で登録された場合は
            自動で繋がらず、<strong>履修は仮アカウント・出席は本人のアカウント</strong>に分かれたまま残ります。
            下の候補から本人のアカウントを選んで繋いでください。
          </p>
          <p style={{fontSize:'0.85rem',color:'#a15',lineHeight:1.7}}>
            統合すると仮アカウント側は削除されます（消えるのは必ず仮アカウントの方で、本人のアカウントは残ります）。
            履修・出席・出席統計は本人のアカウントへ移り、名簿の正しい学籍番号と氏名も引き継がれます。
          </p>

          <div style={{margin:'0.8rem 0'}}>
            <button onClick={doAutoMerge} disabled={mergeBusy}>
              学籍番号が一致するものを一括統合
            </button>
            <span style={{marginLeft:'0.6rem',fontSize:'0.82rem',color:'#777'}}>
              名簿インポート直後にも自動で走ります
            </span>
          </div>

          {mergeMsg && (
            <div style={{background:'#eaf7ee',border:'1px solid #b7e0c4',borderRadius:6,padding:'0.6rem 0.8rem',margin:'0.8rem 0'}}>
              {mergeMsg}
            </div>
          )}

          {residual && (
            <div style={{background:'#fff6e5',border:'1px solid #f0c46b',borderRadius:6,padding:'0.8rem',margin:'0.8rem 0'}}>
              <p style={{margin:'0 0 0.5rem'}}>
                この仮アカウントには、移し替えの対象外のデータが残っています。統合すると<strong>これらは削除されます</strong>。
              </p>
              <ul style={{margin:'0 0 0.6rem 1.2rem'}}>
                {residual.rows.map((r, i) => <li key={i}>{r.table}.{r.column}: {r.rows}件</li>)}
              </ul>
              <button disabled={mergeBusy}
                      onClick={()=>doMerge(residual.placeholderId, residual.targetId, true)}>
                内容を確認したので統合する
              </button>
              <button disabled={mergeBusy} style={{marginLeft:'0.5rem'}} onClick={()=>setResidual(null)}>やめる</button>
            </div>
          )}

          {mergeItems.length === 0 ? (
            <p style={{color:'#888'}}>仮アカウントはありません。</p>
          ) : (
            <table className="data-table">
              <thead><tr><th>仮アカウント（名簿側）</th><th>持っているデータ</th><th>統合先の候補（本人のアカウント）</th></tr></thead>
              <tbody>{mergeItems.map(item => (
                <tr key={item.placeholder.id}>
                  <td>
                    <strong>{item.placeholder.name}</strong>
                    <div style={{fontSize:'0.8rem',color:'#666'}}>{item.placeholder.matchStudentId || item.placeholder.studentId}</div>
                    {item.placeholder.rosterName && item.placeholder.rosterName !== item.placeholder.name && (
                      <div style={{fontSize:'0.75rem',color:'#c67'}}>名簿: {item.placeholder.rosterName}</div>
                    )}
                  </td>
                  <td style={{fontSize:'0.85rem'}}>
                    履修 {item.placeholder.enrollmentCount}件<br/>
                    出席 {item.placeholder.attendanceCount}件
                  </td>
                  <td>
                    {item.candidates.length === 0 ? (
                      <span style={{color:'#888',fontSize:'0.85rem'}}>
                        候補なし（本人がまだサインアップしていない可能性が高い）
                      </span>
                    ) : item.candidates.map(c => (
                      <div key={c.id} style={{marginBottom:'0.5rem',paddingBottom:'0.5rem',borderBottom:'1px dashed #ddd'}}>
                        <strong>{c.name}</strong>
                        <span style={{fontSize:'0.8rem',color:'#666'}}>　ログインID: {c.studentId}　出席 {c.attendanceCount}件</span>
                        {c.rosterName && c.rosterName !== c.name && (
                          <div style={{fontSize:'0.75rem',color:'#c67'}}>名簿: {c.rosterName}</div>
                        )}
                        <div style={{fontSize:'0.78rem',color:'#0a7'}}>{c.reasons.join(' / ')}</div>
                        <button
                          disabled={mergeBusy}
                          style={{marginTop:'0.3rem'}}
                          onClick={()=>{
                            const ok = window.confirm(
                              `仮アカウント「${item.placeholder.name}」を「${c.name}（${c.studentId}）」に統合します。\n` +
                              `仮アカウントは削除され、履修と出席は統合先に移ります。よろしいですか？`
                            );
                            if (ok) doMerge(item.placeholder.id, c.id);
                          }}>
                          このアカウントに統合
                        </button>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* 低出席 */}
      {!loading && tab === 'low' && (
        <div className="card">
          <div className="card-header"><h2>要注意の学生 ({low.length})</h2></div>
          <p style={{fontSize:'0.85rem',color:'#666',lineHeight:1.7}}>
            出席率は「{minSessions}回以上実施した授業」だけで判定します。実施2回で1回欠席すれば50%になってしまい、
            学期はじめは数字が意味を持たないためです。実施回数が少ないうちは累計欠席{absentCount}回・連続欠席{consecutive}回で拾います。
            {includeUnreg && ` まだアプリに登録していない学生（${STATUS.noAccount.label}）も含めています。`}
          </p>
          {low.length === 0 ? <p style={{color:'#888'}}>該当する学生はいません</p> : (
            <table className="data-table">
              <thead><tr>
                <th>学籍番号</th><th>氏名</th><th>授業</th>
                <th>該当理由</th><th>出席率</th><th>出席/実施</th><th>連続欠席</th>
              </tr></thead>
              <tbody>{low.map((s,i) => (
                <tr key={i}>
                  <td>{s.student_id}</td>
                  <td>
                    <strong>{s.name}</strong>
                    <StudentStatusBadges placeholder={s.is_placeholder} size="xs" />
                  </td>
                  <td>{s.class_name || '-'}</td>
                  <td style={{fontSize:'0.85rem'}}>
                    {(s.reasons || []).map((r, k) => (
                      <div key={k} style={{color: s.by_consecutive && k === 0 ? '#c62828' : '#a15'}}>{r}</div>
                    ))}
                  </td>
                  <td>
                    {s.attendance_rate != null
                      ? <strong style={{color: s.by_rate ? '#ef4444' : '#666'}}>
                          {s.attendance_rate}%{!s.by_rate && <span style={{fontSize:'0.7rem'}}>（参考）</span>}
                        </strong>
                      : <span style={{color:'#999'}}>-</span>}
                  </td>
                  <td>{s.present_count} / {s.total_classes}</td>
                  <td>{s.consecutive_absences > 0 ? `${s.consecutive_absences}回` : '-'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* デバイス異常 */}
      {!loading && tab === 'anomalies' && (
        <div className="card">
          <div className="card-header"><h2>デバイス共有疑い</h2></div>
          <p style={{fontSize:'0.85rem',color:'#666',marginTop:0}}>
            同じ端末から、同じ日の同じ授業に2人以上が打刻したものです。「端末」から、その学生の端末を要注意にできます。
          </p>
          {deviceFor && tab === 'anomalies' && (
            <DeviceCheckPanel userId={deviceFor.id} label={deviceFor.label} onClose={() => setDeviceFor(null)} />
          )}
          {anomalies.length === 0 ? <p style={{color:'#888'}}>異常はありません</p> : (
            <table className="data-table">
              <thead><tr><th>日付</th><th>授業</th><th>端末ID</th><th>打刻した学生</th></tr></thead>
              <tbody>{anomalies.map((a,i) => (
                <tr key={i}>
                  <td style={{whiteSpace:'nowrap'}}>{String(a.attendance_date || '').slice(0, 10)}</td>
                  <td>{a.class_name}</td>
                  <td style={{fontFamily:'monospace',fontSize:'0.8rem'}}>{a.device_id ? String(a.device_id).slice(0, 8) + '…' : '-'}</td>
                  <td>
                    {(a.accounts || []).map(x => (
                      <div key={x.user_id} style={{display:'flex',gap:6,alignItems:'center',margin:'2px 0'}}>
                        <span>{x.student_name}（{x.student_id}）</span>
                        <span style={{fontSize:'0.75rem',color:'#888'}}>
                          {x.timestamp ? new Date(x.timestamp).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                        <button style={{padding:'0 0.5rem'}}
                                onClick={() => setDeviceFor({ id: x.user_id, label: `${x.student_name}（${x.student_id}）` })}>端末</button>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* 変更履歴への控えめな入口【2026-09-17】（サイドバーから外した） */}
      <div style={{ textAlign: 'right', fontSize: '0.78rem', margin: '1.2rem 0 0.4rem' }}>
        <Link to="/users-audit" style={{ color: '#9ca3af' }}>
          氏名・学籍番号の変更履歴を見る
        </Link>
      </div>
    </div>
  );
}
